// LeaderboardPage.js — /leaderboards/<slug>
//
// Top-100 board for a single published mini-app. Public read (no auth
// required). Styled to match EndProductDetailPage (the Hub mini-app detail
// page) per user feedback 2026-05-11 — the share-link friends send to
// taunt each other ("I'm rank 4 on Lawrence's Snake, beat me") should look
// like the rest of mchatai.com, not a dark dashboard.
//
// Spec: write-up-an-excellent-witty-sparrow.md Phase 1A.

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from './firebase';
import { collection, query, orderBy, limit, getDocs, doc, getDoc, collectionGroup, where, getDocs as getDocsCG } from 'firebase/firestore';

function formatScore(n, scoreUnit) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  // 'seconds' boards (mchatai-leaderboard-mode: fastest) read as times: 186 → 3:06.
  if (scoreUnit === 'seconds') {
    const t = Math.max(0, Math.round(n));
    const h = Math.floor(t / 3600);
    const m = Math.floor((t % 3600) / 60);
    const s = t % 60;
    const pad = (x) => String(x).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  }
  return n.toLocaleString();
}

function formatRelative(date) {
  if (!date) return '';
  const ms = Date.now() - date.getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function MetaCard({ label, value }) {
  return (
    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px' }}>
      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827' }}>{value}</div>
    </div>
  );
}

export default function LeaderboardPage() {
  const { slug } = useParams();
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boardMeta, setBoardMeta] = useState(null);
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Board metadata FIRST (lastSubmitAt, submitCount, ownerShortUid,
        // projectTitle, iconURL, scoreType/scoreUnit) — scoreType decides the
        // sort direction of the scores query: lowIsGood boards (fastest time,
        // fewest moves) rank the SMALLEST score #1.
        let meta = null;
        const boardSnap = await getDoc(doc(db, `leaderboards/${slug}`));
        if (cancelled) return;
        if (boardSnap.exists()) {
          meta = boardSnap.data();
          setBoardMeta(meta);
          if (meta.ownerShortUid) {
            setProject({
              shortUid: meta.ownerShortUid,
              ownerUid: meta.ownerUid,
              title: meta.projectTitle || null,
              iconURL: meta.iconURL || null,
            });
          }
        }

        const lowIsGood = meta?.scoreType === 'lowIsGood';
        const scoresRef = collection(db, `leaderboards/${slug}/scores`);
        const q = query(scoresRef, orderBy('score', lowIsGood ? 'asc' : 'desc'), limit(100));
        const snap = await getDocs(q);
        if (cancelled) return;
        setScores(snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            displayName: data.displayName || 'Player',
            photoURL: data.photoURL || null,
            score: data.score,
            submittedAt: data.submittedAt?.toDate?.() ?? null,
            platform: data.platform || 'web',
          };
        }));

        // CG-query fallback for boards predating the cached-metadata commit.
        if (!cancelled && !project) {
          try {
            const projSnap = await getDocsCG(
              query(
                collectionGroup(db, 'projects'),
                where('slug', '==', slug),
                where('status', '==', 'live'),
                limit(1)
              )
            );
            if (!cancelled && !projSnap.empty) {
              const d = projSnap.docs[0];
              const path = d.ref.path;
              const uid = path.split('/')[1];
              setProject({ ...d.data(), uid, shortUid: uid.slice(0, 8) });
            }
          } catch (projErr) {
            console.info('[LeaderboardPage] CG fallback failed:', projErr?.code || projErr?.message);
          }
        }
      } catch (err) {
        console.error('[LeaderboardPage] load failed', err);
        if (!cancelled) setError(err.message || 'Failed to load leaderboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // `project` is written by this effect, not read as an input — depending on
    // it would re-run the load every time the load completes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const title = project?.title || project?.name || slug.replace(/-/g, ' ');
  const submitCount = boardMeta?.submitCount || 0;
  const lastRel = boardMeta?.lastSubmitAt?.toDate ? formatRelative(boardMeta.lastSubmitAt.toDate()) : null;
  const topPlayer = scores[0]?.displayName || '—';
  // Only honor a time unit on a board that is actually lower-is-better, so a
  // stale scoreUnit left on a highIsGood board can't format ascending scores as
  // times under a "Score" label (defense-in-depth; the server no longer produces
  // that mixed state, but board docs can be touched by other paths).
  const isTimeBoard = boardMeta?.scoreType === 'lowIsGood' && boardMeta?.scoreUnit === 'seconds';
  const scoreUnit = isTimeBoard ? 'seconds' : null;

  return (
    <div className="mp-page">
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px' }}>
        <Link to="/leaderboards" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.85rem' }}>
          ← All leaderboards
        </Link>
      </div>

      {/* Header — matches EndProductDetailPage layout (icon sidebar + title block). */}
      <section style={{ maxWidth: 800, margin: '0 auto', padding: '24px 24px 0', display: 'flex', gap: 24 }}>
        <div style={{
          width: 88, display: 'flex', flexDirection: 'column', alignItems: 'center',
          paddingTop: 14, flexShrink: 0, background: '#f9fafb', borderRadius: 16, paddingBottom: 14, height: 'fit-content'
        }}>
          {(project?.iconURL) ? (
            <img src={project.iconURL} alt="" style={{ width: 64, height: 64, borderRadius: 14, objectFit: 'cover' }} />
          ) : (
            <div style={{
              width: 64, height: 64, borderRadius: 14,
              background: 'linear-gradient(135deg, #e5e7eb, #d1d5db)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, fontWeight: 800, color: '#6b7280',
            }}>
              {(title.trim()[0] || '?').toUpperCase()}
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af' }}>
              Leaderboard
            </span>
          </div>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#111827', margin: '0 0 8px', textTransform: 'capitalize' }}>
            {title}
          </h1>
          <p style={{ fontSize: '1rem', color: '#6b7280', lineHeight: 1.6, margin: '0 0 20px' }}>
            {isTimeBoard ? 'Fastest times' : 'Top scores'} · {submitCount.toLocaleString()} submission{submitCount === 1 ? '' : 's'}
            {lastRel ? ` · last ${lastRel}` : ''}
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
            <MetaCard label="Submissions" value={submitCount.toLocaleString()} />
            <MetaCard label="Top player" value={topPlayer} />
            <MetaCard label={isTimeBoard ? 'Best time' : 'Top score'} value={scores[0] ? formatScore(scores[0].score, scoreUnit) : '—'} />
            <MetaCard label="Last play" value={lastRel || '—'} />
          </div>

          {project && (
            <div className="detail-actions" style={{ display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap' }}>
              <a
                href={`/u/${project.shortUid}/${slug}/`}
                className="mp-cta-button"
                style={{ padding: '10px 24px', fontSize: '0.9rem' }}
              >
                ▶ Play this game
              </a>
              <Link
                to={`/hub/${slug}`}
                style={{
                  padding: '10px 24px', fontSize: '0.9rem', textDecoration: 'none',
                  background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb',
                  borderRadius: 999, fontWeight: 600, display: 'inline-flex', alignItems: 'center',
                }}
              >
                Make your own version →
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Scores table */}
      <section style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px 40px' }}>
        {loading && <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>}
        {error && (
          <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c' }}>
            {error}
          </div>
        )}

        {!loading && !error && scores.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12 }}>
            <p style={{ color: '#6b7280', margin: '0 0 16px' }}>No scores yet. Be the first!</p>
            {project && (
              <a
                href={`/u/${project.shortUid}/${slug}/`}
                className="mp-cta-button"
                style={{ padding: '10px 24px', fontSize: '0.9rem' }}
              >
                Play now →
              </a>
            )}
          </div>
        )}

        {!loading && scores.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ ...thBase, width: 48 }}>#</th>
                  <th style={thBase}>Player</th>
                  <th style={{ ...thBase, textAlign: 'right', width: 100 }}>{isTimeBoard ? 'Time' : 'Score'}</th>
                  <th style={{ ...thBase, textAlign: 'right', width: 90, color: '#9ca3af' }}>When</th>
                </tr>
              </thead>
              <tbody>
                {scores.map((s, i) => {
                  const rowBase = { borderTop: i === 0 ? 'none' : '1px solid #f3f4f6' };
                  const goldTint = i === 0 ? { background: 'linear-gradient(90deg, rgba(251, 191, 36, 0.12), transparent 60%)' } : null;
                  return (
                    <tr key={s.id} style={{ ...rowBase, ...goldTint }}>
                      <td style={{ padding: '10px 12px', fontVariantNumeric: 'tabular-nums', color: '#6b7280' }}>{i + 1}</td>
                      <td style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {s.photoURL ? (
                          <img src={s.photoURL} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{
                            width: 24, height: 24, borderRadius: '50%',
                            background: '#e5e7eb',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700, color: '#6b7280',
                          }}>{(s.displayName || '?')[0]}</span>
                        )}
                        <span style={{ color: '#111827' }}>{s.displayName}</span>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: '#111827' }}>
                        {formatScore(s.score, scoreUnit)}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 12, color: '#9ca3af' }}>
                        {formatRelative(s.submittedAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const thBase = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#6b7280',
  fontWeight: 600,
};
